import { AnchorHTMLAttributes, DetailedHTMLProps, FC, MouseEventHandler, ReactNode, forwardRef } from 'react'
import { Badge, Box, Drawer, IconType, tss, alpha } from '@ds/components'
import { useDisabledStyles } from '@components/styles'
import EnhancedLink from '@components/EnhancedLink'

type MenuLinkBase = {
    counter?: number
    Icon?: IconType
} & DetailedHTMLProps<Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>, HTMLAnchorElement>

type MenuLinkWithRoute = MenuLinkBase & {
    route: string
    onClick?: MouseEventHandler<HTMLAnchorElement>
}

type MenuLinkWithOnClick = MenuLinkBase & {
    onClick: MouseEventHandler<HTMLAnchorElement>
    route?: never
}

type MenuLinkProps = MenuLinkWithRoute | MenuLinkWithOnClick

const useStyles = tss.create(({ theme }) => ({
    offCanvasMenu: {
        minWidth: 260,
        backgroundColor: '#f5f6f7',
        textTransform: 'uppercase',
    },
    offCanvasMenuItem: {
        display: 'block',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        fontFamily: 'Bryant, sans-serif',
        lineHeight: theme.spacing(5.625),
        fontSize: theme.spacing(1.625),
        fontWeight: 600,
        letterSpacing: theme.spacing(-0.01125),
        textTransform: 'uppercase',
        color: '#0e3247',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        width: '100%',
        border: 'none',
        textAlign: 'left',
        '&:focus': {
            outline: 'none',
        },
        '&:hover': {
            backgroundColor: alpha(theme.palette.action.hover, 0.04),
            textDecoration: 'none',
        },
        '& + &': {
            borderTop: '1px solid #e6e6e6',
        },
    },
    itemWithIcon: {
        display: 'flex',
        alignItems: 'center',
        height: 48,
    },
    borderTop: {
        borderTop: '1px solid #e6e6e6',
    },
    borderBottom: {
        borderBottom: '1px solid #e6e6e6',
    },
}))

const MenuLink = forwardRef<null, MenuLinkProps>(function MenuLink(
    { counter, Icon, children, route, style, onClick, ...anchorProps },
    ref,
) {
    const { classes, cx } = useStyles()

    const inner = (
        <>
            {Icon ? (
                <Box display="flex" alignItems="center" mr={1}>
                    {counter ? (
                        <Badge badgeContent={counter} color="error">
                            <Icon color="primary.dark" />
                        </Badge>
                    ) : (
                        <Icon color="primary.dark" />
                    )}
                </Box>
            ) : null}
            {children}
        </>
    )

    if (!route) {
        return (
            <a
                onClick={onClick}
                {...anchorProps}
                ref={ref}
                className={cx(classes.offCanvasMenuItem, Icon && classes.itemWithIcon, anchorProps.className)}
                data-testid="nav-menu-item"
                style={style}
            >
                {inner}
            </a>
        )
    }

    return (
        <EnhancedLink
            to={route}
            onClick={onClick}
            {...anchorProps}
            ref={ref}
            className={cx(classes.offCanvasMenuItem, Icon && classes.itemWithIcon, anchorProps.className)}
            data-testid="nav-menu-item"
            style={style}
        >
            {inner}
        </EnhancedLink>
    )
})

type MenuButtonProps = {
    Icon?: IconType
    type?: 'button' | 'submit'
    onClick?: () => void
    children?: ReactNode
    isDisabled?: boolean
    className?: string
    borderBottom?: boolean
    borderTop?: boolean
}

const MenuButton: FC<MenuButtonProps> = ({
    Icon,
    onClick,
    children,
    type = 'button',
    isDisabled,
    borderTop,
    borderBottom,
}) => {
    const { classes, cx } = useStyles()
    const { classes: disabledClasses } = useDisabledStyles()

    const combinedClasses = cx(
        classes.offCanvasMenuItem,
        Icon && classes.itemWithIcon,
        isDisabled && disabledClasses.disabled,
        borderTop && classes.borderTop,
        borderBottom && classes.borderBottom,
    )

    return (
        <button
            className={combinedClasses}
            onClick={onClick}
            type={type}
            data-testid="nav-menu-item"
            disabled={isDisabled}
        >
            {Icon ? (
                <Box display="flex" alignItems="center" mr={1}>
                    <Icon color="primary.dark" />
                </Box>
            ) : null}
            {children}
        </button>
    )
}

type OffCanvasMenuProps = {
    open: boolean
    onClose: () => void
    children?: ReactNode
}

const OffCanvasMenu: FC<OffCanvasMenuProps> = ({ open, onClose, children }) => {
    const { classes } = useStyles()

    return (
        <Drawer open={open} onClose={onClose} anchor="right">
            <div className={classes.offCanvasMenu} data-testid="offCanvasMenu">
                {children}
            </div>
        </Drawer>
    )
}

export { MenuButton, MenuLink, OffCanvasMenu }
